import { useState } from "react";
import { apiClient, useAuth } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from "./ui/dialog";
import { toast } from "sonner";
import { Mail, ShieldCheck } from "lucide-react";

/**
 * Universal "change my email" flow with OTP verification. Works for any role
 * (super admin, owner, manager, employee, kiosk-attached employee). Two steps:
 *   step=1 — enter new email, sends OTP to new addr + heads-up to old addr
 *   step=2 — enter 6-digit code, persists, signs out (forces re-login with new email)
 */
const ChangeEmailDialog = ({ open, onOpenChange }) => {
  const { user, logout } = useAuth();
  const [step, setStep] = useState(1);
  const [newEmail, setNewEmail] = useState("");
  const [requestId, setRequestId] = useState("");
  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => {
    setStep(1);
    setNewEmail("");
    setRequestId("");
    setOtp("");
    setLoading(false);
  };

  const handleClose = (next) => {
    if (loading) return;
    if (!next) reset();
    onOpenChange(next);
  };

  const handleRequest = async (e) => {
    e?.preventDefault?.();
    if (!newEmail.includes("@")) {
      toast.error("Enter a valid email address.");
      return;
    }
    if (newEmail.toLowerCase() === user?.email?.toLowerCase()) {
      toast.error("That's already your current email.");
      return;
    }
    setLoading(true);
    try {
      const res = await apiClient.post("/me/email-change/request", { new_email: newEmail });
      setRequestId(res.data.request_id);
      setStep(2);
      toast.success(`Code sent to ${newEmail}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start email change.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e?.preventDefault?.();
    const cleaned = otp.replace(/\D/g, "");
    if (cleaned.length !== 6) {
      toast.error("Enter the 6-digit code from your email.");
      return;
    }
    setLoading(true);
    try {
      await apiClient.post("/me/email-change/verify", { request_id: requestId, otp: cleaned });
      toast.success(`Email changed to ${newEmail}. Please sign in again.`);
      onOpenChange(false);
      // The backend invalidated all other sessions; force a fresh login on the new identity.
      setTimeout(() => logout(), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Verification failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="change-email-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-[#0D9488]" /> Change sign-in email
          </DialogTitle>
          <DialogDescription>
            {step === 1
              ? <>Your current sign-in email is <span className="font-medium text-slate-700">{user?.email}</span>. Enter the new address to receive a verification code.</>
              : <>Enter the 6-digit code we just emailed to <span className="font-medium text-slate-700">{newEmail}</span>. The code expires in 15 minutes.</>}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <form onSubmit={handleRequest} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="new-email">New email</Label>
              <Input
                id="new-email"
                type="email"
                placeholder="you@new-domain.com"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                disabled={loading}
                autoComplete="off"
                data-testid="change-email-new-input"
                autoFocus
              />
            </div>
            <p className="text-xs text-slate-500 flex items-start gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
              For your security, we'll also send a heads-up notice to your current email.
            </p>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || !newEmail} data-testid="change-email-send-otp-btn" className="bg-[#0D9488] hover:bg-[#14B8A6] text-white">
                {loading ? "Sending…" : "Send code"}
              </Button>
            </DialogFooter>
          </form>
        ) : (
          <form onSubmit={handleVerify} className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="otp">Verification code</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                maxLength={6}
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                disabled={loading}
                data-testid="change-email-otp-input"
                className="text-center text-2xl tracking-[0.5em] font-mono"
                autoComplete="one-time-code"
                autoFocus
              />
            </div>
            <button
              type="button"
              onClick={() => { setStep(1); setOtp(""); }}
              disabled={loading}
              className="text-xs text-slate-500 hover:text-slate-700 underline"
              data-testid="change-email-back-btn"
            >
              ← Use a different email
            </button>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)} disabled={loading}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading || otp.length !== 6} data-testid="change-email-verify-btn" className="bg-[#0D9488] hover:bg-[#14B8A6] text-white">
                {loading ? "Verifying…" : "Confirm change"}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ChangeEmailDialog;
